import styled from "styled-components";
import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";

import * as css from "styles/CssVariables.js";
import globalConstants from "constants/globalConstants";
import { AWS_PROFILE_IMAGES_URL } from "constants";

import AdPlansSelection from "./AdPlansSelection";

import adPlanIcon from "assets/img/icons/icons8-web-advertising.svg";
import bronzeCheckmarkIcon from "assets/img/icons/checkmark-bronze.svg";
import silverCheckmarkIcon from "assets/img/icons/checkmark-silver.svg";
import goldCheckmarkIcon from "assets/img/icons/checkmark-gold.svg";
import adPlansIcon from "assets/img/icons/adplans-icon.svg";
import adPlanPerkCoinsIcon from "assets/img/icons/ad-plan-perk-coins-icon.svg";
import adPlanPerkBadgeIcon from "assets/img/icons/ad-plan-perk-badge-icon.svg";
import adPlanPerkVideoIcon from "assets/img/icons/ad-plan-perk-video-icon.svg";
import adPlanPerkChatIcon from "assets/img/icons/ad-plan-perk-chat-icon.svg";
import adPlanPerkPostIcon from "assets/img/icons/ad-plan-perk-post-icon.svg";
import adPlanPerkHighlightIcon from "assets/img/icons/ad-plan-perk-highlight-icon.svg";
import adPlanPerkMessageIcon from "assets/img/icons/ad-plan-perk-message-icon.svg";
import adPlanPerkEmailsIcon from "assets/img/icons/ad-plan-perk-emails.svg";
import johnWhiteProfileImage from "assets/img/adPlanTestimonials/john-white.jpg";
import bridgetDunnProfileImage from "assets/img/adPlanTestimonials/bridget-dunn.png";
import nancyRichmondProfileImage from "assets/img/adPlanTestimonials/nancy-richmond.jpg";
import charleeJoelProfileImage from "assets/img/adPlanTestimonials/charles-joel.png";
import francisAnumaProfileImage from "assets/img/adPlanTestimonials/francis-anuma.png";
import emilyGrayProfileImage from "assets/img/adPlanTestimonials/emily-gray.png";
import quotesWhiteIcon from "assets/img/icons/icons8-quotes-white.svg";

const AdvertisingPlans = styled.div`
  position: relative;
  width: 100%;
  padding: 25px 0;
  box-sizing: border-box;

  background: ${css.BLUE_BOX_GRADIENT};
  border-radius: ${css.BOX_RADIUS_PRIMARY};

  display: flex;
  flex-direction: column;
  gap: ${css.VERTICAL_BOX_MARGIN_LARGE};
`;

const ContentTop = styled.div`
  width: 100%;
  padding: 0 30px;

  box-sizing: border-box;

  display: flex;
  justify-content: space-between;
  gap: 4vw;

  @media (max-width: ${globalConstants.switchMobile}) {
    flex-direction: column;
    padding: 0 20px;
  }
`;

const ContentTopLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${css.VERTICAL_BOX_MARGIN};

  flex: 1;
`;

const BoxTitle = styled.div`
  width: 100%;

  line-height: 130%;

  font-size: ${css.BOX_TITLE_FONT_SIZE};
  font-weight: ${css.BOX_TITLE_FONT_WEIGHT};
`;

const TitleIcon = styled.div`
  display: inline-block;
  padding-right: 6px;

  transform: translateY(4.5px);

  width: 26px;
  min-width: 26px;
`;

const Text = styled.div`
  line-height: 130%;
`;

const ContentTopRight = styled.div`
  padding-top: 20px;
  padding-right: 1vw;

  display: flex;
  align-items: center;

  @media (max-width: ${globalConstants.switchMobile}) {
    padding: 2px 0 0px;

    align-self: center;
  }
`;

const AdPlansImage = styled.div`
  max-width: 90px;
`;

const Perks = styled.div`
  margin-top: 8px;
  padding: 0 30px;

  display: flex;
  flex-direction: column;
  gap: ${css.VERTICAL_BOX_MARGIN};

  @media (max-width: ${globalConstants.switchMobile}) {
    padding: 0 20px;
  }
`;

const Perk = styled.div`
  width: 100%;
  padding: 20px 25px;
  box-sizing: border-box;

  background: ${css.BRAND_BLUSH};
  border-radius: ${css.BOX_RADIUS_SECONDARY};

  display: flex;
  flex-direction: row;
  gap: 20px;

  @media (max-width: ${globalConstants.switchMobile}) {
    flex-direction: column;
    gap: ${css.VERTICAL_BOX_MARGIN};
  }
`;

const PerkImage = styled.div``;

const PerkIcon = styled.div`
  width: 60px;
  min-width: 60px;
  height: 60px;
`;

const PerkContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${css.VERTICAL_BOX_MARGIN};
`;

const PerkTitle = styled.div`
  font-size: 18px;
  color: ${css.DARK_BLUE};
  font-weight: 500;

  white-space: break-spaces;
`;

const PerkText = styled.div`
  color: ${css.DARK_BLUE};
  line-height: 130%;
  font-weight: 400;
`;

const AdPlanTestimonials = styled.div`
  margin-top: 20px;
  padding: 0 30px;

  display: flex;
  flex-direction: column;
  gap: ${css.VERTICAL_BOX_MARGIN_MEDIUM};

  @media (max-width: ${globalConstants.switchMobile}) {
    padding: 0 20px;
  }
`;

const TestimonialsTitle = styled.div`
  line-height: 130%;

  font-size: ${css.BOX_TITLE_FONT_SIZE};
  font-weight: ${css.BOX_TITLE_FONT_WEIGHT};
  text-align: center;
`;

const Testimonials = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  gap: 15px;

  @media (max-width: 800px) {
    flex-direction: column;
    align-items: center;
  }
`;

const TestimonialsColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const Testimonial = styled.div`
  width: 100%;
  max-width: 286px;
  padding: 20px 25px;
  box-sizing: border-box;

  background: ${css.FLAT_BOX_BACKGROUND};
  border-radius: ${css.BOX_RADIUS_SECONDARY};

  display: flex;
  flex-direction: column;
  gap: ${css.VERTICAL_BOX_MARGIN};
`;

const TestimonailHeader = styled.div`
  display: flex;
  flex-direction: column;
`;

const TestimonialHeaderTop = styled.div`
  display: flex;
  justify-content: space-between;
`;

const TestimonialProfileImageWrap = styled.div`
  width: 56px;
  min-width: 56px;
  height: 56px;
`;

const TestimonialProfileImage = styled.img`
  width: 100%;
  border-radius: 50%;
`;

const QuoteImageWrap = styled.div`
  width: 38px;
  min-width: 38px;
`;

const TestimonialHeaderBottom = styled.div`
  display: flex;
  flex-direction: column;
`;

const TestimonialUsernameBox = styled.div`
  padding-top: 5px;

  display: flex;
  align-items: center;
  gap: 5px;
`;

const TestimonialUsername = styled.div`
  padding-top: 4px;

  font-size: 18px;
  font-weight: 500;
`;

const TestimonialCheckmarkIcon = styled.div`
  padding-top: 6px;

  width: 20px;
  min-width: 20px;
  height: 20px;
`;

const TestimonialUserSubheading = styled.div`
  padding-top: 0;

  font-size: 15px;
  color: ${css.TEXT_GREY};
`;

const TestimonialText = styled.div`
  font-size: 16px;
  font-style: italic;
  font-weight: 300;
  line-height: 130%;
`;

const TextSpacer = styled.div`
  height: 12px;
`;

const AdPlans = styled.div`
  margin-top: 20px;
  padding: 0 30px;

  display: flex;
  flex-direction: column;
  gap: ${css.VERTICAL_BOX_MARGIN_MEDIUM};

  @media (max-width: ${globalConstants.switchMobile}) {
    padding: 0 20px;
  }
`;

const AdPlansTitle = styled.div`
  line-height: 130%;

  font-size: ${css.BOX_TITLE_FONT_SIZE};
  font-weight: ${css.BOX_TITLE_FONT_WEIGHT};
`;

const AdPlansInfo = styled.div`
  padding: 10px 30px;

  text-align: center;

  line-height: 130%;

  align-self: center;

  @media (max-width: ${globalConstants.switchMobile}) {
    padding: 10px 20px;
  }
`;

const AdPlansInfoLink = styled(Link)`
  display: inline-block;

  text-decoration: none;
  color: ${css.LIGHT_BLUE};
`;

const RecentPaymentsStrip = styled.div`
  width: 100%;
  max-width: 100%;
  padding: 15px 0;
  margin-top: 20px;
  overflow: hidden;

  background-color: ${css.BRAND_BLUSH};
`;

const RecentPaymentsMarquee = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const RecentPayments = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const RecentPayment = styled.div`
  width: 120px;
  min-width: 120px;
  height: 120px;
  padding: 10px;
  box-sizing: border-box;

  border-radius: 60px;
  background-color: ${css.FLAT_BOX_BACKGROUND};

  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
`;

const RecentPaymentImageWrap = styled.div`
  position: relative;
`;

const RecentPaymentProfileImage = styled.div`
  width: 52px;
  min-width: 52px;
  height: 52px;
  background-image: ${(props) => `url(${props.image})`};
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  border-radius: 50%;
`;

// const RecentPaymentProfileImage = styled.img`
//   width: 100%;
//   border-radius: 50%;
// `;

const RecentPaymentAdPlanIcon = styled.div`
  position: absolute;
  right: 0;
  bottom: 0;

  width: 18px;
  min-width: 18px;
  height: 18px;

  transform: translate(5px, 5px);
`;

const RecentPaymentBottom = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1px;
`;

const RecentPaymentName = styled.div`
  max-width: 100px;

  font-size: 13px;
  font-weight: 500;
  text-align: center;

  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const RecentPaymentInfo = styled.div`
  padding: 0 20px;

  font-size: 11px;
  font-weight: 300;
  text-align: center;
  line-height: 110%;
`;

export default ({ data, onPlanSelect, recentPaymentsData }) => {
  const container = useRef();

  const { contextSafe } = useGSAP({ scope: container });

  const recentPayments =
    recentPaymentsData.recentAdPlanPayments.recentPayments.map((payment) => {
      const profilePictureURL = AWS_PROFILE_IMAGES_URL + payment.profile_image;

      let adPlanIcon;

      switch (payment.item) {
        case "bronze_ad_plan":
          adPlanIcon = bronzeCheckmarkIcon;
          break;
        case "silver_ad_plan":
          adPlanIcon = silverCheckmarkIcon;
          break;
        case "gold_20_ad_plan":
          adPlanIcon = goldCheckmarkIcon;
          break;
        case "gold_40_ad_plan":
          adPlanIcon = goldCheckmarkIcon;
          break;
        case "gold_60_ad_plan":
          adPlanIcon = goldCheckmarkIcon;
          break;
        case "gold_80_ad_plan":
          adPlanIcon = goldCheckmarkIcon;
          break;
        case "gold_100_ad_plan":
          adPlanIcon = goldCheckmarkIcon;
          break;
      }

      let paymentInfo = "Subscribed recently";

      if (payment.reccuring) {
        paymentInfo = "Renewed recently";
      }

      return (
        <RecentPayment key={payment.created_at}>
          <RecentPaymentImageWrap>
            <RecentPaymentProfileImage image={profilePictureURL}>
              {/* <RecentPaymentProfileImage
                src={profilePictureURL}
                style={{ width: "100%" }}
              /> */}
            </RecentPaymentProfileImage>
            <RecentPaymentAdPlanIcon>
              <img src={adPlanIcon} style={{ width: "100%" }} />
            </RecentPaymentAdPlanIcon>
          </RecentPaymentImageWrap>
          <RecentPaymentBottom>
            <RecentPaymentName>{payment.username}</RecentPaymentName>
            <RecentPaymentInfo>{paymentInfo}</RecentPaymentInfo>
          </RecentPaymentBottom>
        </RecentPayment>
      );
    });

  const runMarquee = contextSafe(() => {
    const marquee = document.getElementsByClassName("recent-payments-marquee");

    const group = marquee[0].firstElementChild;

    const groupWidth =
      parseInt(getComputedStyle(group).getPropertyValue("width"), 10) + 10;

    let tl = gsap.timeline({ paused: true, repeat: -1 });

    tl.fromTo(
      marquee[0],
      { x: 0 },
      {
        x: -groupWidth,
        duration: 60,
        ease: "none",
      }
    );

    tl.play();
  });

  useEffect(() => {
    runMarquee();
  }, []);

  return (
    <AdvertisingPlans ref={container}>
      <ContentTop>
        <ContentTopLeft>
          <BoxTitle>
            <TitleIcon>
              <img src={adPlanIcon} style={{ width: "100%" }} />
            </TitleIcon>
            Stand Out with Our Advertising Plans
          </BoxTitle>
          <Text>
            Maximize your potential on the Scalelup platform with our exclusive
            Advertising Plans. Designed to maximize your reach and engagement,
            our plans offer a range of premium features that enhance your
            visibility and interaction on the platform. With a Scalelup Ad Plan,
            you prove that you are operating a serious business.
          </Text>
        </ContentTopLeft>
        <ContentTopRight>
          <AdPlansImage>
            <img
              src={adPlansIcon}
              alt="Advertising Plans"
              style={{ width: "100%" }}
            />
          </AdPlansImage>
        </ContentTopRight>
      </ContentTop>
      <Perks>
        <Perk>
          <PerkImage>
            <PerkIcon>
              <img src={adPlanPerkCoinsIcon} style={{ width: "100%" }} />
            </PerkIcon>
          </PerkImage>
          <PerkContent>
            <PerkTitle>Up to 100,000 Coins Daily</PerkTitle>
            <PerkText>
              Receive a daily allocation of Coins to keep your promotional
              activities ongoing. These Coins allow you to maintain consistent
              visibility and engagement without additional cost, ensuring your
              business remains in the spotlight
            </PerkText>
          </PerkContent>
        </Perk>
        <Perk>
          <PerkImage>
            <PerkIcon>
              <img src={adPlanPerkBadgeIcon} style={{ width: "100%" }} />
            </PerkIcon>
          </PerkImage>
          <PerkContent>
            <PerkTitle>Checkmark Badge</PerkTitle>
            <PerkText>
              Stand out with a Checkmark badge next to your name. This symbol,
              reflective of your chosen plan, boosts your credibility and helps
              you gain trust and recognition among other users, enhancing your
              professional image within the Scalelup community.
            </PerkText>
          </PerkContent>
        </Perk>
        <Perk>
          <PerkImage>
            <PerkIcon>
              <img src={adPlanPerkVideoIcon} style={{ width: "100%" }} />
            </PerkIcon>
          </PerkImage>
          <PerkContent>
            <PerkTitle>Video Posts</PerkTitle>
            <PerkText>
              Engage your audience more dynamically by including a video in your
              posts. Videos capture attention and convey information more
              effectively, allowing you to showcase your products or services in
              a compelling format.
            </PerkText>
          </PerkContent>
        </Perk>
        <Perk>
          <PerkImage>
            <PerkIcon>
              <img src={adPlanPerkChatIcon} style={{ width: "100%" }} />
            </PerkIcon>
          </PerkImage>
          <PerkContent>
            <PerkTitle>Increased Chat Limits</PerkTitle>
            <PerkText>
              Expand your networking potential with higher daily chat initiation
              limits. Connect with more users each day, fostering valuable
              relationships and generating new business opportunities, while
              gaining diverse insights to enrich your strategy.
            </PerkText>
          </PerkContent>
        </Perk>
        <Perk>
          <PerkImage>
            <PerkIcon>
              <img src={adPlanPerkPostIcon} style={{ width: "100%" }} />
            </PerkIcon>
          </PerkImage>
          <PerkContent>
            <PerkTitle>Extended Post Length</PerkTitle>
            <PerkText>
              Enjoy the flexibility to create posts up to three times longer
              than regular ones. This feature allows you to provide more
              detailed information, tell a richer story, and better communicate
              your value proposition to your audience.
            </PerkText>
          </PerkContent>
        </Perk>
        <Perk>
          <PerkImage>
            <PerkIcon>
              <img src={adPlanPerkHighlightIcon} style={{ width: "100%" }} />
            </PerkIcon>
          </PerkImage>
          <PerkContent>
            <PerkTitle>Highlighted Posts</PerkTitle>
            <PerkText>
              Make your posts stand out with the Highlighted Post feature. These
              posts are visually more prominent, drawing more attention and
              encouraging higher interaction rates from the community,
              ultimately increasing visibility and engagement with your target
              audience.
            </PerkText>
          </PerkContent>
        </Perk>
        <Perk>
          <PerkImage>
            <PerkIcon>
              <img src={adPlanPerkMessageIcon} style={{ width: "100%" }} />
            </PerkIcon>
          </PerkImage>
          <PerkContent>
            <PerkTitle>Sponsored Messages</PerkTitle>
            <PerkText>
              Reach out to users who have exhausted their available posts by
              showing Sponsored Message. These messages are delivered without
              any Coin deduction, providing a cost-effective way to capture
              attention and convey your business message directly.
            </PerkText>
          </PerkContent>
        </Perk>
        <Perk>
          <PerkImage>
            <PerkIcon>
              <img src={adPlanPerkEmailsIcon} style={{ width: "100%" }} />
            </PerkIcon>
          </PerkImage>
          <PerkContent>
            <PerkTitle>Autoresponder</PerkTitle>
            <PerkText>
              Set up an autoresponder and send a series of personalized emails
              to every new contact who signs up through your Landing Page. The
              autoresponder helps you build trust, educate your leads, and drive
              conversions - all while saving time.
            </PerkText>
          </PerkContent>
        </Perk>
      </Perks>
      <AdPlanTestimonials>
        <TestimonialsTitle>What to Expect from an Ad Plan</TestimonialsTitle>
        <Testimonials>
          <TestimonialsColumn>
            <Testimonial>
              <TestimonailHeader>
                <TestimonialHeaderTop>
                  <TestimonialProfileImageWrap>
                    <TestimonialProfileImage
                      src={johnWhiteProfileImage}
                      style={{ width: "100%" }}
                    />
                  </TestimonialProfileImageWrap>
                  <QuoteImageWrap>
                    <img src={quotesWhiteIcon} style={{ width: "100%" }} />
                  </QuoteImageWrap>
                </TestimonialHeaderTop>
                <TestimonialHeaderBottom>
                  <TestimonialUsernameBox>
                    <TestimonialUsername>John White</TestimonialUsername>
                    <TestimonialCheckmarkIcon>
                      <img src={goldCheckmarkIcon} style={{ width: "100%" }} />
                    </TestimonialCheckmarkIcon>
                  </TestimonialUsernameBox>
                  <TestimonialUserSubheading>
                    Entrepreneur
                  </TestimonialUserSubheading>
                </TestimonialHeaderBottom>
              </TestimonailHeader>
              <TestimonialText>
                Having the Checkmark badge next to my name has boosted my
                credibility immensely. It helps set me apart as a trustworthy
                and reputable business, which has been invaluable in building
                client trust. Sales originating from Scalelup have increased by
                40%.
              </TestimonialText>
            </Testimonial>
            <Testimonial>
              <TestimonailHeader>
                <TestimonialHeaderTop>
                  <TestimonialProfileImageWrap>
                    <TestimonialProfileImage
                      src={nancyRichmondProfileImage}
                      style={{ width: "100%" }}
                    />
                  </TestimonialProfileImageWrap>
                  <QuoteImageWrap>
                    <img src={quotesWhiteIcon} style={{ width: "100%" }} />
                  </QuoteImageWrap>
                </TestimonialHeaderTop>
                <TestimonialHeaderBottom>
                  <TestimonialUsernameBox>
                    <TestimonialUsername>Nancy Richmond</TestimonialUsername>
                    <TestimonialCheckmarkIcon>
                      <img src={goldCheckmarkIcon} style={{ width: "100%" }} />
                    </TestimonialCheckmarkIcon>
                  </TestimonialUsernameBox>
                  <TestimonialUserSubheading>
                    Travel Consultant
                  </TestimonialUserSubheading>
                </TestimonialHeaderBottom>
              </TestimonailHeader>
              <TestimonialText>
                With the Gold Plan, I've been able to reach a broader audience
                and generate more interest in my business. The increased chat
                limits help me make new connections daily, leading to more
                contacts and promising leads.
                <TextSpacer />
                Thanks to the Ad plan, I'm now closing more successful deals
                every day.
              </TestimonialText>
            </Testimonial>
            <Testimonial>
              <TestimonailHeader>
                <TestimonialHeaderTop>
                  <TestimonialProfileImageWrap>
                    <TestimonialProfileImage
                      src={francisAnumaProfileImage}
                      style={{ width: "100%" }}
                    />
                  </TestimonialProfileImageWrap>
                  <QuoteImageWrap>
                    <img src={quotesWhiteIcon} style={{ width: "100%" }} />
                  </QuoteImageWrap>
                </TestimonialHeaderTop>
                <TestimonialHeaderBottom>
                  <TestimonialUsernameBox>
                    <TestimonialUsername>Francis Anuma</TestimonialUsername>
                    <TestimonialCheckmarkIcon>
                      <img
                        src={silverCheckmarkIcon}
                        style={{ width: "100%" }}
                      />
                    </TestimonialCheckmarkIcon>
                  </TestimonialUsernameBox>
                  <TestimonialUserSubheading>
                    Network Marketer
                  </TestimonialUserSubheading>
                </TestimonialHeaderBottom>
              </TestimonailHeader>
              <TestimonialText>
                Opting for the Scalelup Ad Plan has been one of the best
                decisions for my business. The combination of increased
                visibility, more website traffic, and the enhanced reputation
                has significantly boosted my business growth.
              </TestimonialText>
            </Testimonial>
          </TestimonialsColumn>
          <TestimonialsColumn>
            <Testimonial>
              <TestimonailHeader>
                <TestimonialHeaderTop>
                  <TestimonialProfileImageWrap>
                    <TestimonialProfileImage
                      src={bridgetDunnProfileImage}
                      style={{ width: "100%" }}
                    />
                  </TestimonialProfileImageWrap>
                  <QuoteImageWrap>
                    <img src={quotesWhiteIcon} style={{ width: "100%" }} />
                  </QuoteImageWrap>
                </TestimonialHeaderTop>
                <TestimonialHeaderBottom>
                  <TestimonialUsernameBox>
                    <TestimonialUsername>Bridget Dunn</TestimonialUsername>
                    <TestimonialCheckmarkIcon>
                      <img
                        src={silverCheckmarkIcon}
                        style={{ width: "100%" }}
                      />
                    </TestimonialCheckmarkIcon>
                  </TestimonialUsernameBox>
                  <TestimonialUserSubheading>
                    E-commerce Store Owner
                  </TestimonialUserSubheading>
                </TestimonialHeaderBottom>
              </TestimonailHeader>
              <TestimonialText>
                Since I started using the Ad Plan, my posts have become far more
                visible. The steady daily Coins ensure my content consistently
                reaches a large audience, resulting in a noticeable uptick in
                the number of views my posts receive. Not to mention the better
                price for coins compared to purchasing them individually.
              </TestimonialText>
            </Testimonial>
            <Testimonial>
              <TestimonailHeader>
                <TestimonialHeaderTop>
                  <TestimonialProfileImageWrap>
                    <TestimonialProfileImage
                      src={charleeJoelProfileImage}
                      style={{ width: "100%" }}
                    />
                  </TestimonialProfileImageWrap>
                  <QuoteImageWrap>
                    <img src={quotesWhiteIcon} style={{ width: "100%" }} />
                  </QuoteImageWrap>
                </TestimonialHeaderTop>
                <TestimonialHeaderBottom>
                  <TestimonialUsernameBox>
                    <TestimonialUsername>Charlee T. Joel</TestimonialUsername>
                    <TestimonialCheckmarkIcon>
                      <img
                        src={bronzeCheckmarkIcon}
                        style={{ width: "100%" }}
                      />
                    </TestimonialCheckmarkIcon>
                  </TestimonialUsernameBox>
                  <TestimonialUserSubheading>
                    Home Business Owner
                  </TestimonialUserSubheading>
                </TestimonialHeaderBottom>
              </TestimonailHeader>
              <TestimonialText>
                The exposure I've gained through Scalelup Ad plan has led to a
                significant increase in clicks on my website links. This boost
                in engagement has driven valuable traffic to my site, enhancing
                my sales and conversion rates.
              </TestimonialText>
            </Testimonial>
            <Testimonial>
              <TestimonailHeader>
                <TestimonialHeaderTop>
                  <TestimonialProfileImageWrap>
                    <TestimonialProfileImage
                      src={emilyGrayProfileImage}
                      style={{ width: "100%" }}
                    />
                  </TestimonialProfileImageWrap>
                  <QuoteImageWrap>
                    <img src={quotesWhiteIcon} style={{ width: "100%" }} />
                  </QuoteImageWrap>
                </TestimonialHeaderTop>
                <TestimonialHeaderBottom>
                  <TestimonialUsernameBox>
                    <TestimonialUsername>Emily Gray</TestimonialUsername>
                    <TestimonialCheckmarkIcon>
                      <img src={goldCheckmarkIcon} style={{ width: "100%" }} />
                    </TestimonialCheckmarkIcon>
                  </TestimonialUsernameBox>
                  <TestimonialUserSubheading>
                    Freelancer
                  </TestimonialUserSubheading>
                </TestimonialHeaderBottom>
              </TestimonailHeader>
              <TestimonialText>
                The ability to create longer posts and include videos has
                allowed me to communicate my message more effectively. My
                audience is more engaged, and my content truly stands out now.
                <TextSpacer />
                More people are interacting with my posts, which is bringing me
                greater profits.
              </TestimonialText>
            </Testimonial>
          </TestimonialsColumn>
        </Testimonials>
      </AdPlanTestimonials>
      <RecentPaymentsStrip>
        <RecentPaymentsMarquee className="recent-payments-marquee">
          <RecentPayments>{recentPayments}</RecentPayments>
          <RecentPayments>{recentPayments}</RecentPayments>
        </RecentPaymentsMarquee>
      </RecentPaymentsStrip>
      <AdPlans>
        <AdPlansTitle>Choose an Advertising Plan</AdPlansTitle>
        <AdPlansSelection
          prices={data.adPlanBillingData.prices}
          onPlanSelect={onPlanSelect}
        />
      </AdPlans>
      <AdPlansInfo>
        For a detailed description of the features, please visit{" "}
        <AdPlansInfoLink
          to="https://help.scalelup.com/en/articles/10962832-detailed-overview-of-scalelup-advertising-plan-features"
          target="_blank"
        >
          this article
        </AdPlansInfoLink>
        . You can cancel at anytime. By making a payment, you agree to the
        Refund Policy outlined in our{" "}
        <AdPlansInfoLink to="/terms" target="_blank">
          Terms
        </AdPlansInfoLink>
        .
      </AdPlansInfo>
    </AdvertisingPlans>
  );
};
